"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { logoSmall } from "@/utils/image";

const PrivacyPolicy = () => {
  const [domain, setDomain] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDomain(window.location.origin);
    }
  }, []);

  return (
    <div className="py-5">
      <Link href="/login" className="d-flex justify-content-center mb-4">
        <Image src={logoSmall} alt="logo" />
      </Link>
      <div className="dashOuter">
        <h4 className="fs20 fw600 mb-4 text-center">Privacy Policy</h4>
        <div className="text-start">
          <p>Effective Date: February 1, 2026</p>
          <p>Last Updated: February 7, 2026</p>
          <p>
            Privacy Policy URL:{" "}
            <Link href="/privacy-policy" className="text-primary">
              {domain}/privacy
            </Link>
          </p>
          <p>
            Terms URL:{" "}
            <Link href="/terms-condition" className="text-primary">
              {domain}/terms-condition
            </Link>
          </p>
          <p>
            Applies To: Hivora Circle mobile and web applications, websites, and
            related services (collectively, “Hivora Circle,” “we,” “us,” “our”).
          </p>

          <p className="mt-4">
            Hivora Circle is a peer-validated matchmaking platform where users can
            invite a trusted “Circle of Trust” to help review and approve
            matches. This Privacy Policy explains what information we collect,
            how we use it, how we protect it, and your rights and choices.
          </p>

          <p className="fw-bold">
            By using Hivora Circle, you agree to this Privacy Policy.
          </p>

          <h5 className="mt-4 fw-bold">1) Definitions</h5>
          <p>
            <strong>Personal Information</strong> means information that
            identifies you, relates to you, describes you, or can reasonably be
            linked to you (for example: name, email, phone number, photos,
            device identifiers, or account activity).
          </p>
          <p>
            <strong>Service Providers</strong> (also called “processors” or
            “sub-processors”) are companies we use to help operate Hivora Circle
            (for example: hosting, SMS delivery, payments, and app reliability
            services). They may process Personal Information only on our
            instructions and only to provide services to Hivora Circle.
          </p>

          <h5 className="mt-4 fw-bold">2) Who We Are and Who This Is For</h5>
          <p>
            Hivora Circle is intended for adults 18 years of age or older. We do
            not knowingly collect Personal Information from anyone under 18. If
            you believe a minor has provided information, contact us so we can
            delete it.
          </p>
          <p>
            Controller/Operator: Innovative Technology Enterprise Strategic
            Solutions (iTESS), LLC
            <br />
            Business Address: 11808 Edmond Woods Way, Bowie, Maryland 20721,
            United States
          </p>

          <h5 className="mt-4 fw-bold">3) Information We Collect</h5>
          <p>
            We collect information in three ways:
            <br />
            (A) information you provide, (B) information collected
            automatically, and (C) information from other users/your Circle (as
            part of Hivora Circle functionality).
          </p>

          <h6 className="mt-3 fw-bold">A) Information You Provide</h6>
          <p className="fw-bold mb-1">Account & Contact Information</p>
          <ul>
            <li>Name (or display name/username)</li>
            <li>Email address</li>
            <li>
              Phone number (for verification, account security, and Circle
              invites)
            </li>
            <li>Date of birth or age confirmation (18+)</li>
          </ul>

          <p className="fw-bold mb-1">Profile Information</p>
          <ul>
            <li>Photos you upload</li>
            <li>City/region and other profile fields you choose to complete</li>
            <li>Compatibility and lifestyle questionnaire answers</li>
            <li>Preferences and “what you’re looking for” selections</li>
            <li>Bio text or other content you add to your profile</li>
          </ul>

          <p className="fw-bold mb-1">Circle of Trust Information</p>
          <ul>
            <li>
              Names and contact details (phone/email) of Circle members you
              invite
            </li>
            <li>Circle membership settings</li>
            <li>
              Circle actions: approvals/declines, feedback (where enabled), and
              timestamps
            </li>
          </ul>

          <p className="fw-bold mb-1">Communications & Support</p>
          <ul>
            <li>
              Messages or content you send in-app (if/when messaging is enabled)
            </li>
            <li>
              Reports you submit, support requests, and related correspondence
            </li>
          </ul>

          <p className="fw-bold mb-1">Payment Information (If Applicable)</p>
          <p>
            If Hivora Circle offers paid features (subscriptions, tokens/credits,
            etc.), purchases are processed by third-party payment processors. We
            receive records such as plan type, transaction date/time, and
            payment status. We do not store full card numbers.
          </p>

          <h6 className="mt-3 fw-bold">
            B) Information We Collect Automatically
          </h6>
          <p className="fw-bold mb-1">Device & App Activity</p>
          <ul>
            <li>Device type, operating system, app version</li>
            <li>Browser type (web), language settings</li>
            <li>App events (feature usage, screens viewed), timestamps</li>
            <li>Diagnostic data (crash logs, performance metrics)</li>
          </ul>

          <p className="fw-bold mb-1">Network & Location</p>
          <ul>
            <li>IP address</li>
            <li>Approximate location inferred from IP</li>
            <li>
              If you enable it, precise device location (you can disable this in
              device settings)
            </li>
          </ul>

          <p className="fw-bold mb-1">Cookies (Web)</p>
          <p>We may use cookies or similar technologies for:</p>
          <ul>
            <li>Login/session management</li>
            <li>Security and fraud prevention</li>
            <li>Performance measurement and basic analytics</li>
          </ul>

          <h6 className="mt-3 fw-bold">
            C) Information From Other Users / Your Circle
          </h6>
          <p>Because Hivora Circle is social-by-design, we process:</p>
          <ul>
            <li>
              Circle member decisions and feedback (as permitted by your
              settings)
            </li>
            <li>Reports from other users about safety or policy violations</li>
            <li>
              Interaction signals (e.g., match approvals, invites, acceptances)
            </li>
          </ul>

          <h5 className="mt-4 fw-bold">4) How We Use Your Information</h5>
          <p>
            We use your information to operate Hivora Circle, provide core
            features, keep the platform safe, and improve reliability.
          </p>

          <h6 className="mt-3 fw-bold">A) Provide and Maintain the Service</h6>
          <ul>
            <li>Create and manage your account</li>
            <li>Verify phone/email and secure logins</li>
            <li>
              Build your profile and enable matching/compatibility features
            </li>
            <li>Send and manage Circle invitations</li>
            <li>Display and manage Circle reviews/approvals</li>
            <li>Provide customer support</li>
          </ul>

          <h6 className="mt-3 fw-bold">B) Safety, Integrity, and Security</h6>
          <ul>
            <li>Detect, prevent, and investigate fraud/spam/abuse</li>
            <li>
              Enforce our Terms, community guidelines, and trust & safety rules
            </li>
            <li>Secure our systems and users</li>
          </ul>

          <h6 className="mt-3 fw-bold">
            C) Service Communications (Not Third-Party Marketing)
          </h6>
          <p>We may send service messages by email or SMS such as:</p>
          <ul>
            <li>Verification codes and login alerts</li>
            <li>Circle invitations and Circle activity notifications</li>
            <li>Account/security updates and essential service notices</li>
          </ul>
          <p>
            We do not use your Personal Information to send third-party
            marketing.
            <br />
            We do not share your Personal Information with third parties for
            their marketing or advertising.
          </p>

          <h6 className="mt-3 fw-bold">D) Improve and Develop Hivora Circle</h6>
          <ul>
            <li>Debug, troubleshoot, and improve performance</li>
            <li>Understand feature usage in aggregated form</li>
            <li>Run internal testing and quality assurance</li>
          </ul>

          <h5 className="mt-4 fw-bold">5) Text Messages (SMS) and Consent</h5>
          <p>
            If you provide your phone number, Hivora Circle may send SMS messages
            for:
          </p>
          <ul>
            <li>Account verification and security</li>
            <li>Circle invitations</li>
            <li>Critical account notifications (where enabled)</li>
          </ul>
          <p>
            Where required, we obtain consent through clear disclosures during
            onboarding or feature use. You can opt out by replying STOP (and
            reply HELP for assistance). Message and data rates may apply.
          </p>

          <h5 className="mt-4 fw-bold">6) Sharing of Information</h5>
          <h6 className="mt-3 fw-bold">
            A) No Sale / No Third-Party Marketing Sharing
          </h6>
          <p>
            Hivora Circle does not sell Personal Information.
            <br />
            Hivora Circle does not share Personal Information with third parties
            for their marketing or advertising purposes.
          </p>

          <h6 className="mt-3 fw-bold">
            B) Service Providers (Limited Sharing to Run the App)
          </h6>
          <p>
            We may share limited information with vetted Service Providers who
            help us operate Hivora Circle (for example: cloud hosting, SMS
            delivery, fraud prevention, customer support tools, app performance
            monitoring, and payment processing, if applicable). These providers
            are contractually restricted to:
          </p>
          <ul>
            <li>Use data only to provide services to Hivora Circle</li>
            <li>Keep it confidential and secure</li>
            <li>Not sell it or use it for their own marketing</li>
          </ul>

          <h6 className="mt-3 fw-bold">C) Legal and Safety Disclosures</h6>
          <p>
            We may disclose information if required to comply with law, legal
            process, valid government requests, or to protect rights, safety,
            and security (for example: investigating fraud or abuse).
          </p>

          <h6 className="mt-3 fw-bold">D) Business Transfers</h6>
          <p>
            If we are involved in a merger, acquisition, financing,
            reorganization, bankruptcy, or sale of assets, information may be
            transferred as part of that transaction. We will provide notice
            where required.
          </p>

          <h5 className="mt-4 fw-bold">
            7) SDKs and Service Providers We Use (Transparency List)
          </h5>
          <p>
            Hivora Circle may use the following providers/SDKs to operate core
            functions. This list may change as we improve Hivora Circle.
          </p>

          <p className="fw-bold mb-1">Cloud Hosting & Infrastructure</p>
          <p>
            Amazon Web Services (AWS) – hosting, databases, storage, security
            logging/monitoring.
          </p>

          <p className="fw-bold mb-1">SMS / Phone Verification</p>
          <p>
            Twilio Verify / Twilio Messaging – phone verification (one-time
            codes), Circle invitation SMS, and account/security SMS.
          </p>

          <p className="fw-bold mb-1">
            App Reliability & Analytics (Performance Only)
          </p>
          <p>
            Firebase (Google) – may include services such as Crashlytics (crash
            reporting), Analytics (app usage metrics), Cloud Messaging (push
            notifications), Remote Config (feature toggles), and/or
            Authentication (if enabled).
            <br />
            Note: We configure these tools for product performance and
            reliability. We do not allow them to use your data for their own
            advertising purposes.
          </p>

          <p className="fw-bold mb-1">Payments (If Applicable)</p>
          <p>
            Stripe – payment processing for subscriptions, tokens/credits, or
            other paid features. Stripe receives payment details; Hivora Circle
            does not store full card numbers.
          </p>

          <p className="fw-bold mb-1">Platform Providers</p>
          <p>
            Apple and Google – may process information necessary for app
            distribution, device services, and sign-in flows where enabled
            (e.g., Sign in with Apple / Google Sign-In).
          </p>
          <p>
            If you have questions about any provider listed above, contact us
            and we will explain what data is involved and why.
          </p>

          <h5 className="mt-4 fw-bold">8) App Tracking and Advertising</h5>
          <p>Hivora Circle is built to prioritize privacy.</p>
          <ul>
            <li>
              <strong>No targeted advertising:</strong> We do not use your
              Personal Information to deliver third-party targeted ads.
            </li>
            <li>
              <strong>
                No “tracking” across apps/websites for advertising:
              </strong>{" "}
              We do not track you across apps or websites owned by other
              companies for advertising purposes. If we ever introduce tracking,
              we will request permission where required (e.g., Apple’s App
              Tracking Transparency prompts).
            </li>
            <li>
              <strong>Analytics is for performance:</strong> Any analytics we
              use is intended to measure app performance and reliability, not to
              create advertising profiles.
            </li>
          </ul>

          <h5 className="mt-4 fw-bold">
            9) Your Choices, Rights, and Controls
          </h5>
          <h6 className="mt-3 fw-bold">
            A) Access, Correction, and Portability
          </h6>
          <p>
            You can review and update key account/profile information in the app
            settings. You may also request access to certain information we hold
            about you by contacting us.
          </p>

          <h6 className="mt-3 fw-bold">B) Communication Preferences</h6>
          <ul>
            <li>
              Essential service messages (security, verification, system
              notices) may not be fully optional.
            </li>
            <li>Where available, you can opt out of non-essential messages.</li>
            <li>
              For SMS (where enabled): reply STOP to opt out and HELP for
              assistance.
            </li>
          </ul>

          <h6 className="mt-3 fw-bold">C) Location Permissions</h6>
          <p>
            You can disable precise location access in your device settings.
          </p>

          <h6 className="mt-3 fw-bold">D) Delete Your Account and Data</h6>
          <p>Hivora Circle provides:</p>
          <ul>
            <li>In-app account deletion pathway, and</li>
            <li>
              A web-based deletion request link:{" "}
              <Link href="/delete-account" className="text-primary">
                {domain}/delete-account
              </Link>
            </li>
          </ul>
          <p>
            When you request deletion, we delete your account and associated
            Personal Information within a reasonable period, subject to limited
            exceptions described in “Data Retention.”
          </p>

          <h5 className="mt-4 fw-bold">10) Data Retention</h5>
          <p>We retain Personal Information only as long as necessary to:</p>
          <ul>
            <li>Provide the service</li>
            <li>Maintain security and prevent fraud/abuse</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
          </ul>
          <p>
            Deletion exceptions: We may retain limited information (e.g., audit
            logs, transaction records, abuse-prevention records) if required by
            law or for legitimate security and fraud-prevention purposes.
          </p>

          <h5 className="mt-4 fw-bold">11) Security</h5>
          <p>
            We use reasonable administrative, technical, and physical safeguards
            designed to protect your information, including access controls,
            encryption in transit, monitoring, and secure vendor management. No
            method of transmission or storage is completely secure—please use
            caution when sharing information.
          </p>

          <h5 className="mt-4 fw-bold">
            12) Data About Circle Members You Invite
          </h5>
          <p>
            If you invite a Circle member, you confirm you have permission to
            provide their contact details for the purpose of sending the invite
            and enabling Hivora Circle functionality. We use that contact
            information only to deliver invitations and enable Circle
            participation—not for third-party marketing.
          </p>

          <h5 className="mt-4 fw-bold">
            13) U.S. State Privacy Rights (Where Applicable)
          </h5>
          <p>Depending on where you live, you may have rights such as:</p>
          <ul>
            <li>Request access to Personal Information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion (subject to legal/security exceptions)</li>
            <li>Appeal certain decisions (where required)</li>
          </ul>
          <p>
            To exercise a privacy right, contact us at the email below. We may
            need to verify your identity before fulfilling a request.
          </p>

          <h5 className="mt-4 fw-bold">14) International Users</h5>
          <p>
            If you access Hivora Circle from outside the United States, your
            information may be transferred to and processed in the United States
            or other jurisdictions where our Service Providers operate. Those
            jurisdictions may have different privacy laws than your home
            country.
          </p>

          <h5 className="mt-4 fw-bold">15) Third-Party Links and Services</h5>
          <p>
            Hivora Circle may link to third-party websites or services. Their
            privacy practices are governed by their own policies, not ours.
          </p>

          <h5 className="mt-4 fw-bold">16) Changes to This Privacy Policy</h5>
          <p>
            We may update this Privacy Policy periodically. We will revise the
            Effective Date and, when appropriate, provide additional notice in
            the app or on our website.
          </p>

          <h5 className="mt-4 fw-bold">17) Contact Us</h5>
          <p>
            Privacy Contact: support@hivora.ai
            <br />
            Support Contact: support@hivora.ai
            <br />
            Mailing Address: 11808 Edmond Woods Way, Bowie, Maryland 20721,
            United States
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
